import classNames from 'classnames';
import { format } from 'date-fns';
import padStart from 'lodash-es/padStart';
import uniq from 'lodash-es/uniq';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { NoResult } from '@/common/components/shared/no-result/NoResult';
import type { ReportNumberOfEmployeeMonthMetric } from '@/common/graphql';
import type {
  ClientReportNumberOfEmployeeItem,
  ClientReportNumberOfEmployeeMetric,
  ColumnComputationResult,
  ColumnDataProps,
  ReportNumberOfEmployee,
  ReportNumberOfEmployeeItem,
} from './props';
import { SumByYearMonthCell } from './SumByYearMonthCell';
import { TableRow } from './TableRow';

const columnNameTotal = 'Total';

export const metricsToMap = (
  metrics: Array<ReportNumberOfEmployeeMonthMetric> | undefined,
): ClientReportNumberOfEmployeeMetric => Object.fromEntries((metrics ?? []).map(m => [m.yearMonth, String(m.count)]));

interface NumberOfEmployeeTableByYearMonthProps {
  type: 'yearMonth';
  /** Standard monthly tabs: anchor year for skeleton columns */
  startYearMonth: string;
}

interface NumberOfEmployeeTableByColumnKeysProps {
  type: 'columnKeys';
  /** VDP/VDD tabs: explicit column keys (overrides startYearMonth + yearMonthList) */
  columnKeys: string[];
  /** VDP/VDD tabs: explicit footer row values keyed by columnKey */
  totalRow: { [key: string]: string };
  /** VDP/VDD tabs: returns true for the column that should be highlighted */
  isCurrentColumn: (key: string) => boolean;
}

type NumberOfEmployeeTableProps<T extends ReportNumberOfEmployeeItem> = {
  type: string;
  expandable?: boolean;
  converter: (item: T) => ClientReportNumberOfEmployeeItem;
  data: ReportNumberOfEmployee<T> | undefined;
  headerFirstColumn: ReactNode;
  loading: boolean;
} & (NumberOfEmployeeTableByYearMonthProps | NumberOfEmployeeTableByColumnKeysProps);

export const NumberOfEmployeesTable = <T extends ReportNumberOfEmployeeItem>({
  converter,
  data,
  expandable,
  headerFirstColumn,
  loading,
  ...props
}: NumberOfEmployeeTableProps<T>) => {
  const [expandedRows, setExpandedRows] = useState<Array<string>>([]);
  const allExpandableRowIds = useMemo(
    () => (data?.allIds || []).filter(it => it.split('|').length < (data?.maxDepth ?? 1)),
    [data?.allIds, data?.maxDepth],
  );

  const onExpandAll = useCallback(() => setExpandedRows(allExpandableRowIds), [allExpandableRowIds]);
  const onCollapseAll = useCallback(() => setExpandedRows([]), []);
  const onClickDimension = useCallback((id: string) => {
    setExpandedRows(prev => {
      if (prev.some(it => it === id)) {
        return prev.filter(it => it !== id);
      }

      return uniq([...prev, id]);
    });
  }, []);

  const isVisibleExpandAll = useMemo(
    () => expandedRows.length < allExpandableRowIds.length,
    [allExpandableRowIds.length, expandedRows.length],
  );
  const isVisibleCollapseAll = useMemo(
    () => expandedRows.length > 0 && expandedRows.some(it => !it.includes('|')),
    [expandedRows],
  );

  const tableData = useMemo((): Array<ClientReportNumberOfEmployeeItem> => {
    if (loading) {
      return Array.from({ length: 3 }).map((_, index) => ({
        id: `empty${index}`,
        dimension: `empty${index}`,
        dimensionText: '',
        metrics: {},
        children: null,
      }));
    }

    return data?.items.map(item => converter(item)) || [];
  }, [converter, data?.items, loading]);

  if (!loading && !data) {
    return <NoResult description="Try changing the filters" title="No data found" />;
  }

  const renderTable = ({ columnList, sumByYearMonth, isCurrentColumn }: ColumnComputationResult) => (
    <>
      {!loading && expandable && (
        <div className="pl-4 pb-4 inline-flex items-center gap-2">
          {isVisibleExpandAll && (
            <div className="text-link cursor-pointer select-none" onClick={onExpandAll}>
              Expand all
            </div>
          )}
          {isVisibleExpandAll && isVisibleCollapseAll && <span className="text-3xs text-gray-6">|</span>}
          {isVisibleCollapseAll && (
            <div className="text-link cursor-pointer select-none" onClick={onCollapseAll}>
              Collapse all
            </div>
          )}
        </div>
      )}
      <div className="overflow-auto text-nowrap text-2xs">
        <table className="min-w-full table-fixed border-separate border-spacing-0 [&_tr]:h-10 [&_th]:px-2 [&_td]:px-2 [&_th]:py-0 [&_td]:py-0 [&_th]:border-y [&_th]:border-r [&_td]:border-b [&_td]:border-r">
          <thead className="sticky top-0 z-10">
            <tr>
              <th className="!pl-4 bg-gray-0 text-left lg:sticky lg:left-0 text-3xs">{headerFirstColumn}</th>
              {columnList.map(col => (
                <th
                  key={col}
                  className={classNames('text-right tabular-nums', {
                    'bg-amber-50': isCurrentColumn(col),
                    'bg-gray-0': !isCurrentColumn(col),
                  })}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.map(it => (
              <TableRow
                key={it.id}
                isCurrentColumn={isCurrentColumn}
                data={data}
                depth={0}
                expandable={expandable}
                expandedRows={expandedRows}
                item={it}
                loading={loading}
                yearMonths={columnList}
                onClickDimension={onClickDimension}
              />
            ))}
          </tbody>
          <tfoot>
            {tableData.length > 0 ? (
              <tr className="bg-gray-0">
                <td className="!pl-4 bg-gray-0 lg:sticky lg:left-0 font-semibold">{columnNameTotal}</td>
                {!loading
                  ? sumByYearMonth.map(([yearMonth, total]) => (
                      <SumByYearMonthCell
                        key={yearMonth}
                        yearMonth={yearMonth}
                        value={total}
                        isCurrentColumn={isCurrentColumn}
                        loading={loading}
                      />
                    ))
                  : columnList.map(yearMonth => (
                      <SumByYearMonthCell
                        key={yearMonth}
                        yearMonth={yearMonth}
                        value=""
                        isCurrentColumn={isCurrentColumn}
                        loading={loading}
                      />
                    ))}
              </tr>
            ) : (
              <tr>
                <td colSpan={columnList.length + 1}>
                  <NoResult
                    description="Try changing the filters"
                    title="No data found"
                    titleClassName="text-sm text-gray-9"
                    wrapperClassName="py-10"
                  />
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>
    </>
  );

  return props.type === 'columnKeys' ? (
    <ColumnKeysColumnData
      columnKeys={props.columnKeys}
      isCurrentColumn={props.isCurrentColumn}
      totalRow={props.totalRow}
      renderer={renderTable}
    />
  ) : (
    <YearMonthColumnData
      loading={loading}
      startYearMonth={props.startYearMonth}
      total={data?.total}
      yearMonthList={data?.yearMonthList}
      renderer={renderTable}
    />
  );
};

interface YearMonthColumnDataProps extends ColumnDataProps {
  startYearMonth: string;
  loading: boolean;
  yearMonthList: Array<string> | undefined;
  total: Array<ReportNumberOfEmployeeMonthMetric> | undefined;
}

const YearMonthColumnData = ({ startYearMonth, loading, yearMonthList, total, renderer }: YearMonthColumnDataProps) => {
  const columnList = useMemo(() => {
    if (loading) {
      return Array.from(
        { length: 12 },
        (_, i) => `${startYearMonth.slice(0, 4)}-${padStart((i + 1).toString(), 2, '0')}`,
      );
    }

    return yearMonthList || [];
  }, [startYearMonth, loading, yearMonthList]);

  const sumByYearMonth = useMemo(() => Object.entries(metricsToMap(total)), [total]);

  const isCurrentColumn = useCallback((key: string) => key === format(new Date(), 'yyyy-MM'), []);

  return renderer({ columnList, sumByYearMonth, isCurrentColumn });
};

interface ColumnKeysColumnDataProps extends ColumnDataProps {
  columnKeys: string[];
  totalRow: { [key: string]: string };
  isCurrentColumn: (key: string) => boolean;
}

const ColumnKeysColumnData = ({ columnKeys, totalRow, isCurrentColumn, renderer }: ColumnKeysColumnDataProps) => {
  const sumByYearMonth = useMemo(() => Object.entries(totalRow), [totalRow]);

  return renderer({ columnList: columnKeys, sumByYearMonth, isCurrentColumn });
};
